import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { Upload, X, FileText, Image as ImgIcon } from "lucide-react";

const MAX_MB = 3;
const ALLOWED = ["image/", "application/pdf"];

/**
 * Simple photo/PDF picker. onChange returns array of upload URLs.
 */
export default function FileUploader({ value = [], onChange, max = 6 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    if (value.length + files.length > max) {
      toast.error(`Máximo ${max} arquivos por pedido`);
      return;
    }
    setUploading(true);
    const newUrls = [];
    for (const file of files) {
      if (!ALLOWED.some((p) => file.type.startsWith(p))) {
        toast.error(`Tipo não suportado: ${file.name}`);
        continue;
      }
      if (file.size / (1024 * 1024) > MAX_MB) {
        toast.error(`${file.name} maior que ${MAX_MB}MB`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrls.push({ url: data.url, content_type: data.content_type, name: file.name });
      } catch (err) {
        logger.error("upload failed", err);
        toast.error(`Falha ao enviar ${file.name}`);
      }
    }
    setUploading(false);
    if (newUrls.length) onChange([...value, ...newUrls]);
  };

  const remove = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map((item, idx) => (
          <div
            key={item.url}
            data-testid={`upload-item-${idx}`}
            className="relative w-20 h-20 bg-secondary rounded-lg overflow-hidden border border-border"
          >
            {item.content_type?.startsWith("image/") ? (
              <img src={item.url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground p-1">
                <FileText size={22} />
                <span className="text-[9px] mt-1 truncate w-full text-center">{item.name}</span>
              </div>
            )}
            <button
              onClick={() => remove(idx)}
              type="button"
              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-500"
              data-testid={`upload-remove-${idx}`}
            >
              <X size={12} />
            </button>
          </div>
        ))}
        {value.length < max && (
          <button
            type="button"
            data-testid="upload-add"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="w-20 h-20 rounded-lg border-2 border-dashed border-border hover:border-primary/60 flex flex-col items-center justify-center gap-1 text-muted-foreground transition-colors"
          >
            {uploading ? (
              <span className="text-xs">...</span>
            ) : (
              <>
                <Upload size={18} />
                <span className="text-[10px]">Anexar</span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => handleFiles(Array.from(e.target.files || []))}
      />
      <p className="text-[10px] text-muted-foreground flex items-center gap-1">
        <ImgIcon size={10} /> Fotos, prints ou PDF · até {MAX_MB}MB cada · máx {max}
      </p>
    </div>
  );
}
